import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import { USER_ACTIONS } from "components/Actions/types";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import QueryDropdown from "components/QueryDropdown";
import { Role as RoleType } from "components/TableDetails/UserDetails/Permissions/types";
import ParamsContext from "context/params/paramsContext";
import {
    DROPDOWN_SIZES,
    ENDPOINTS,
    ID_TYPE,
    isInternalTypeCompany,
    noop,
    useApi,
    useIsMounted,
    useUserAccessLevels
} from "shared";

import { ExpirationDate, Role } from "../../components";
import { UserActionDialogProps } from "../types";

const ResendInvitation = ({ isOpen, close, data }: UserActionDialogProps) => {
    const { rootCompanyType } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { handleResponse, postData } = useApi();
    const { isAllowedToAssignAccessLevel } = useUserAccessLevels();
    const isMounted = useIsMounted();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [expiresAt, setExpiresAt] = useState<string | null>(null);
    const [hasExpiresAtError, setHasExpiresAtError] = useState(false);

    const isRootInternalTypeCompany = isInternalTypeCompany(rootCompanyType);

    useEffect(() => {
        data && setExpiresAt(data.expires_at);
    }, [data]);

    const handleCloseDialog = () => close(USER_ACTIONS.ResendInvitation);

    const handleExpirationDateChange = (newExpDate: string | null) =>
        setExpiresAt(newExpDate);

    const handleExpirationDateError = (error: string | null) =>
        setHasExpiresAtError(error !== null);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const { email, company_id, access_level, roles } = data;

            const payload = {
                email,
                company_id,
                expires_at: expiresAt,
                ...(isAllowedToAssignAccessLevel()
                    ? {
                          access_level
                      }
                    : {}),
                ...(!isRootInternalTypeCompany
                    ? { role_ids: roles.map((role: RoleType) => role.id) }
                    : {})
            };

            const response = await postData(ENDPOINTS.Users, payload);

            if (isMounted) {
                handleCloseDialog();
                handleResponse(response);
            }
        } catch (error) {
            if (isMounted) {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    };

    return (
        <Dialog
            title={t("Dialog##resend user invitation")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={handleCloseDialog}
            submit={handleSubmit}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    isReadyToConfirm={!hasExpiresAtError}
                    confirmationButtonText={t("Button##confirm")}
                    cancelButtonTestId="cancel-resend-invite"
                    confirmationButtonTestId="confirm-resend-invite"
                />
            }
        >
            <InputField
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                required
                readOnly
                labelLeft={t("Dialog##email")}
                defaultValue={data?.email}
            />

            <QueryDropdown
                resource=""
                idType={ID_TYPE.Id}
                isRequired
                size={DROPDOWN_SIZES.Medium}
                fullWidth
                value={data?.company ?? null}
                change={noop}
                labelLeft={t("Dialog##company")}
                readOnly
                customStyle={{ margin: "16px 0" }}
            />

            <Role
                isReadOnly
                isLoading={false}
                roleResource=""
                roles={data?.roles ?? []}
                companyValue={data?.company ?? null}
                onChange={noop}
            />

            <ExpirationDate
                expirationDate={expiresAt}
                isActive
                isLoading={isLoading}
                onExpirationDateChange={handleExpirationDateChange}
                onExpirationDateError={handleExpirationDateError}
                customStyle={{ marginBottom: "16px" }}
            />
        </Dialog>
    );
};

export default ResendInvitation;
